import axiosInstance from '../utils/axiosConfig';

// HMS Internal Admin Service - for managing the internal HR system
class HMSAdminService {
  constructor() {
    // No need for baseURL since axiosInstance already has it configured
  }

  // Check if current user is superuser
  async checkSuperuserStatus() {
    try {
      const response = await axiosInstance.get('/api/check-admin-status/');
      return response.data;
    } catch (error) {
      console.error('Error checking superuser status:', error);
      throw error;
    }
  }

  // Get employees list with pagination and filters
  async getEmployees({ page = 1, search = '', active = undefined, position = '', department = '', office = '', is_active = undefined } = {}) {
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', '100'); // Load up to 100 employees to get all at once

      if (search) {
        params.append('search', search);
      }

      if (active !== undefined) {
        params.append('active', active.toString());
      }

      if (is_active !== undefined) {
        params.append('is_active', is_active.toString());
      }

      if (position) {
        params.append('position', position);
      }

      if (department) {
        params.append('department', department);
      }

      if (office) {
        params.append('office', office);
      }

      const response = await axiosInstance.get(`/api/admin/hms/employees/list/?${params}`);

      // Transform the response to match the expected format
      return {
        results: response.data.employees,
        total_pages: response.data.pagination.total_pages,
        current_page: response.data.pagination.current_page,
        total_count: response.data.pagination.total_count
      };
    } catch (error) {
      console.error('Error fetching employees:', error);
      throw error;
    }
  }

  // Create new employee (minimal data)
  async createEmployee({ 
    email, 
    first_name, 
    last_name, 
    password, 
    is_superuser = false,
    is_supervisor = false,              // NEW
    is_head_of_department = false,      // NEW
    is_crm_approver = false,            // NEW
    is_kyc_approver = false,            // NEW
    is_credit_approver = false,         // NEW
    is_financial_approver = false,      // NEW
    supervised_department_id = null,    // NEW
    auto_generate_password = false,     // NEW: server generates a strong password if true
    send_invite_email = true            // NEW: email login details to the new employee
  }) {
    try {
      const response = await axiosInstance.post('/api/admin/hms/employees/create/', {
        email,
        first_name,
        last_name,
        password,
        is_superuser,
        is_supervisor,
        is_head_of_department,
        is_crm_approver,
        is_kyc_approver,
        is_credit_approver,
        is_financial_approver,
        supervised_department_id,
        auto_generate_password,
        send_invite_email
      });
      return response.data;
    } catch (error) {
      console.error('Error creating employee:', error);
      throw error;
    }
  }

  // Bulk upload employees from CSV file
  async bulkUploadEmployees(csvFile, { sendInviteEmail = true } = {}) {
    try {
      const formData = new FormData();
      formData.append('csv_file', csvFile);
      formData.append('send_invite_email', sendInviteEmail ? 'true' : 'false');

      const response = await axiosInstance.post(
        '/api/admin/hms/employees/bulk-upload/',
        formData,
        { 
          headers: { 
            'Content-Type': 'multipart/form-data' 
          } 
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error bulk uploading employees:', error);
      throw error;
    }
  }

  // Export employees to Excel (.xlsx) — returns full response so callers can read headers
  async exportEmployeesXlsx({ fields, includeInactive = false }) {
    try {
      const response = await axiosInstance.post(
        '/api/admin/hms/employees/export/',
        {
          fields,
          include_inactive: includeInactive,
        },
        { responseType: 'blob' }
      );
      return response;
    } catch (error) {
      console.error('Error exporting employees:', error);
      throw error;
    }
  }

  // Deactivate employee
  async deactivateEmployee(employeeId) {
    try {
      const response = await axiosInstance.post(`/api/admin/hms/employees/${employeeId}/deactivate/`);
      return response.data;
    } catch (error) {
      console.error('Error deactivating employee:', error);
      throw error;
    }
  }

  // Activate employee
  async activateEmployee(employeeId) {
    try {
      const response = await axiosInstance.post(`/api/admin/hms/employees/${employeeId}/activate/`);
      return response.data;
    } catch (error) {
      console.error('Error activating employee:', error);
      throw error;
    }
  }

  // Get deactivated employees list
  async getDeactivatedEmployees({ page = 1, search = '' } = {}) {
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', '50');
      
      if (search) {
        params.append('search', search);
      }
      
      const response = await axiosInstance.get(`/api/admin/hms/employees/deactivated/?${params}`);
      
      return {
        results: response.data.employees,
        total_pages: response.data.pagination.total_pages,
        current_page: response.data.pagination.current_page,
        total_count: response.data.pagination.total_count
      };
    } catch (error) {
      console.error('Error fetching deactivated employees:', error);
      throw error;
    }
  }

  // Get dashboard statistics
  async getDashboardStats() {
    try {
      const response = await axiosInstance.get('/api/admin/hms/dashboard/stats/');
      
      // Transform the response to match the expected format
      const stats = response.data.employee_stats;
      const activity = response.data.recent_activity;
      return {
        total_employees: stats.total,
        active_employees: stats.active,
        inactive_employees: stats.inactive,
        with_hms_id: stats.with_hms_id,
        without_hms_id: stats.without_hms_id,
        new_employees_30d: activity.new_employees_30d,
        recent_logins_30d: activity.recent_logins_30d,
        department_breakdown: response.data.department_breakdown,
        office_breakdown: response.data.office_breakdown
      };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  }

  // Get audit logs
  async getAuditLogs({ page = 1 } = {}) {
    try {
      const response = await axiosInstance.get(`/api/admin/hms/audit-logs/?page=${page}`);
      
      // Transform the response to match the expected format
      return {
        results: response.data.logs,
        total_pages: response.data.pagination.total_pages,
        current_page: response.data.pagination.current_page,
        total_count: response.data.pagination.total_count
      };
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      throw error;
    }
  }

  // Get dropdown options for profile forms
  async getDropdownOptions() {
    try {
      const response = await axiosInstance.get('/api/profile/dropdown-options/');
      return response.data;
    } catch (error) {
      console.error('Error fetching dropdown options:', error);
      throw error;
    }
  }

  // Reset employee password
  async resetEmployeePassword({ employee_id, new_password }) {
    try {
      const response = await axiosInstance.post('/api/admin/hms/employees/reset-password/', {
        employee_id,
        new_password
      });
      return response.data;
    } catch (error) {
      console.error('Error resetting employee password:', error);
      throw error;
    }
  }

  // Assign employee role (admin only)
  /**
   * Assign a role / access flags.
   *
   * Passes the caller's payload STRAIGHT THROUGH. It used to destructure a
   * fixed list of fields and rebuild the body from them, which meant any flag
   * added to the Role Assignment modal but not also added here was dropped
   * silently: the toggle ticked, the request 200'd, and the server answered
   * "No changes detected" (hit for real by has_offline_access). The server
   * already treats an absent/None field as "no change", so forwarding the
   * object is behaviourally identical for every existing caller — and the next
   * flag cannot go missing on the way out.
   *
   * That fixed the LAST leg only. The caller above it went on rebuilding the
   * body from a typed-out list of names, in two blocks that had to be kept
   * identical, so the same bug survived one level up (can_approve_templates was
   * dropped there for its entire life). HMSAdminDashboard now builds the body
   * from the form itself via buildRoleAssignmentPayload, so no step in this
   * round trip names a field any more.
   */
  async assignEmployeeRole(payload) {
    try {
      const response = await axiosInstance.post('/api/admin/hms/employees/assign-role/', payload);
      return response.data;
    } catch (error) {
      console.error('Error assigning employee role:', error);
      throw error;
    }
  }

  // Position verification system methods
  async getPositionVerificationRequests(status = 'pending') {
    try {
      const response = await axiosInstance.get('/api/admin/position/requests/', {
        params: { status }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching position requests:', error);
      throw error;
    }
  }

  async reviewPositionRequest(requestId, action, notes = '') {
    try {
      const response = await axiosInstance.post(`/api/admin/position/requests/${requestId}/review/`, {
        action, // 'approve' or 'deny'
        notes
      });
      return response.data;
    } catch (error) {
      console.error('Error reviewing position request:', error);
      throw error;
    }
  }

  // Leave audit logs methods
  async getLeaveAuditLogs({ page = 1, action = 'all', employee_id = null } = {}) {
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', '50');

      if (action && action !== 'all') {
        params.append('action', action);
      }

      if (employee_id) {
        params.append('employee_id', employee_id.toString());
      }

      const response = await axiosInstance.get(`/api/leave/admin/audit-logs/?${params}`);
      
      // Transform the response to match the expected format
      return {
        results: response.data.logs,
        total_pages: response.data.pagination.total_pages,
        current_page: response.data.pagination.current_page,
        total_count: response.data.pagination.total_count
      };
    } catch (error) {
      console.error('Error fetching leave audit logs:', error);
      throw error;
    }
  }

  // Supervisor user assignment methods
  async assignUsersToSupervisor({ supervisor_id, user_ids, unassign = false }) {
    try {
      const response = await axiosInstance.post(`/api/admin/hms/supervisors/${supervisor_id}/assign-users/`, {
        user_ids,
        unassign
      });
      return response.data;
    } catch (error) {
      console.error('Error assigning users to supervisor:', error);
      throw error;
    }
  }

  async getSupervisorAssignments(supervisor_id) {
    try {
      const response = await axiosInstance.get(`/api/admin/hms/supervisors/${supervisor_id}/assignments/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching supervisor assignments:', error);
      throw error;
    }
  }

  // HOD user assignment methods
  async assignUsersToHOD({ hod_id, user_ids, unassign = false }) {
    try {
      const response = await axiosInstance.post(`/api/admin/hms/hods/${hod_id}/assign-users/`, {
        user_ids,
        unassign
      });
      return response.data;
    } catch (error) {
      console.error('Error assigning users to HOD:', error);
      throw error;
    }
  }

  async getHODAssignments(hod_id) {
    try {
      const response = await axiosInstance.get(`/api/admin/hms/hods/${hod_id}/assignments/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching HOD assignments:', error);
      throw error;
    }
  }

  async assignUsersToLeaveSupervisor({ leave_supervisor_id, user_ids, unassign = false }) {
    try {
      const response = await axiosInstance.post(`/api/admin/hms/leave-supervisors/${leave_supervisor_id}/assign-users/`, {
        user_ids,
        unassign
      });
      return response.data;
    } catch (error) {
      console.error('Error assigning users to leave supervisor:', error);
      throw error;
    }
  }

  async getLeaveSupervisorAssignments(leave_supervisor_id) {
    try {
      const response = await axiosInstance.get(`/api/admin/hms/leave-supervisors/${leave_supervisor_id}/assignments/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching leave supervisor assignments:', error);
      throw error;
    }
  }

  // Get reportee counts for an employee (for role unassignment warning)
  async getEmployeeReporteeCounts(employee_id) {
    try {
      const response = await axiosInstance.get(`/api/admin/hms/employees/${employee_id}/reportee-counts/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching employee reportee counts:', error);
      throw error;
    }
  }
}

const hmsAdminService = new HMSAdminService();
export default hmsAdminService;
